import type { UpdateContactInput } from 'app/generated/gql/graphql'
import { observer } from 'mobx-react-lite'
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { LockSimpleIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import {
  Box as BoxIcon,
  Calculator,
  Calendar,
  Crown,
  EditPencil,
  Link as LinkIcon,
  PageEdit as PageEditIcon,
  Refresh,
  Union,
  UserPlus,
  ViewColumns3,
} from 'iconoir-react'
import React, { ReactElement, useState } from 'react'
import { ActivityJson } from 'app/types/entities'
import CircularProgressBar from '../CircularProgressBar'
import { useConfirmation } from '../ConfirmationContext'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { Tooltip } from '../Tooltip'
import { MarkPatientModal } from './MarkPatientModal'
import ContactSearchModalLink from 'app/components/contacts/ContactSearchModalLink'
import { CreateNoteModal } from 'app/components/contacts/drawer/CreateNoteModal'
import useFakeProgress from 'app/hooks/fakeProgress'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { ScheduleAppointmentDialog } from 'app/components/scheduling/inapp/ScheduleAppointmentDialog'
import { getScheduleEligibility } from 'lib/scheduleEligibility'
import type { ContactJson } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import type { PatientJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/**
 * This component has the following rules
 * - Integrated but not linked to Patient:
 *    - Mark as Non-Patient
 *    - Link to Patient
 * - Integrated but linked to a Patient
 *    - Mark as Non-Patient
 *    - Remove Patient Link
 *  - Not Integrated but not marked as patient
 *    - Mark as Non-Patient
 *    - Mark as Patient
 * - Not Integrated but marked as Patient
 *    - Mark as Non-Patient
 *    - Update Patient Data
 *    - Mark as Lead
 *
 * Integrated refers when a contact has sikkaId
 *
 * @Prop contact. The contact object
 * @Prop patients. The list of patients that are linked to the contact
 * @Prop isIntegrated. If the contact location has sikkaOfficeId or carestackId
 * @Prop disabled.
 * @Prop onMarkNonPatient. Callback when the user clicks on mark as non-patient
 * @Prop onMarkPatient. Callback when the user clicks on mark as patient
 * @Prop onMarkLead. Callback when the user clicks on mark as lead
 * @returns
 */

// observer: reads authStore.user via useUser() in render, so the component must be an observer.
export const ContactActions = observer(function ContactActions({
  contact,
  patients,
  location,
  isIntegrated,
  disabled = false,
  onMarkNonPatient,
  onMarkPatient,
  onMarkLead,
  onChange,
  onCustomizeDrawer,
  onActivityAdded,
}: {
  contact: ContactJson
  patients?: PatientJson[]
  location?: LocationJson
  isIntegrated?: boolean
  disabled?: boolean
  onMarkNonPatient: () => void
  onMarkPatient: (contactData: UpdateContactInput) => void
  onMarkLead?: () => void
  onChange?: (contact: ContactJson) => void
  onCustomizeDrawer?: () => void
  onActivityAdded?: (activity: ActivityJson) => void
}): ReactElement {
  const [confirm] = useConfirmation()

  // v2 drawer doesn't always pass `patients`, but `contact.patientIds` still indicates a linked patient.
  const isLinkedPatient = !!(isIntegrated && ((patients?.length ?? 0) > 0 || (contact.patientIds?.length ?? 0) > 0))
  const schedule = getScheduleEligibility(contact, location)
  const canSchedule = schedule.state === `ready`
  const user = useUser()
  const checkPermission = useCheckPermission()
  const contactStore = useContactStore()
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  if (!user) return <></>
  const isSuperUser = user.type === `super`

  // Permission checks for contact actions
  const hasMergePermission = checkPermission(`contacts:merge`)
  const hasMarkNonPatientPermission = checkPermission([`org:contacts:set-non-patient`, `contacts:mark-non-patient`])
  const handleMarkNonPatient = async () => {
    if (isLinkedPatient) return
    await confirm({
      title: `Mark as Non-Patient`,
      description: `Marking this contact will prevent this contact from being shown on dashboards and in calculations as well as delete crucial data including lifecycle and lead status.\n\nThis functionality SHOULD NOT be used to close or disqualify a lead. `,
      variant: `danger`,
    }).then(onMarkNonPatient)
  }
  // const handleRemovePatientLink = async () => {
  //   await confirm({
  //     title: `Remove Patient Link`,
  //     description: `Are you sure you want to remove this contacts link to a patient record? By doing so it will rebuild the contact from scratch. Please note that during the rebuild we will still run our matching logic which might result in a match to a patient record.`,
  //     variant: `danger`,
  //   }).then(onRemovePatientLink)
  // }
  const handleMarkLead = async () => {
    await confirm({
      title: `Mark as Lead`,
      description: `Are you sure you want to mark this patient as a lead? By doing so it will update the lifecycle and lead status as well as remove any financial numbers associated with the contact.

The following fields will be updated:
      * Lifecycle Stage from Patient to Qualified Lead
      * Lead Status from Closed Won to In Progress`,
      variant: `danger`,
    }).then(onMarkLead)
  }

  const handleRebuild = async () => {
    await confirm({
      title: `Rebuild Contact`,
      description: `Are you sure you want to rebuild this contact? This will reprocess the contact data using the merge contacts function and may update various fields.`,
      variant: `danger`,
    }).then(async () => {
      // Create standalone progress modal component
      // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
      const RebuildProgressModal = () => {
        const [progress, progressStart, progressEnd] = useFakeProgress()

        React.useEffect(() => {
          progressStart()
        }, [])

        React.useEffect(() => {
          // Start the rebuild operation
          const doRebuild = async () => {
            try {
              const result = await contactStore.recalculateContact(contact.id!)
              const rebuilt = (result.data as any)?.recalculateContact as ContactJson | undefined
              progressEnd()
              setTimeout(() => {
                closeModal()
                if (rebuilt) {
                  notifySuccess(`Contact rebuilt successfully`)
                  onChange?.(rebuilt)
                }
              }, 1000)
            } catch (error) {
              logError(`Failed to rebuild contact:`, error)
              progressEnd()
              closeModal()
              notifyError(`Failed to rebuild contact`)
            }
          }
          bg(() => doRebuild())
        }, [])

        return (
          <div className="mx-auto my-4 flex grow flex-col items-center justify-center space-y-4">
            <CircularProgressBar progress={progress} />
            <p className="text-heading-md">{progress !== 100 ? `Rebuilding in progress...` : `Successfully rebuilt`}</p>
          </div>
        )
      }

      // Show progress modal
      showModal(<RebuildProgressModal />, {
        title: `Rebuilding Contact`,
        isFullScreen: false,
        showActions: false,
        useRadix: true,
      })
    })
  }

  const handleRecalculate = async () => {
    await confirm({
      title: `Recalculate Contact`,
      description: `Are you sure you want to recalculate this contact? This will save the contact without explicit changes, triggering recalculation of dependent fields.`,
      variant: `danger`,
    }).then(async () => {
      setIsRecalculating(true)
      try {
        const result = await contactStore.recalculateContact(contact.id!)
        const recalculated = (result.data as any)?.recalculateContact as ContactJson | undefined
        if (recalculated) {
          notifySuccess(`Contact recalculated successfully`)
          onChange?.(recalculated)
        }
      } catch (error) {
        logError(`Failed to recalculate contact:`, error)
        notifyError(`Failed to recalculate contact`)
      } finally {
        setIsRecalculating(false)
      }
    })
  }

  function showNoteModal() {
    showModal(<CreateNoteModal contact={contact} onActivityAdded={onActivityAdded} />, {
      title: `Add new note`,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })
  }

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={classNames(
              `btn btn-tertiary btn-sm flex aspect-square items-center justify-center p-0`,
              disabled && `cursor-not-allowed`,
            )}
            disabled={disabled}
          >
            <EllipsisVerticalIcon height={16} width={16} className="text-default" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-0 shadow-elevation-2">
          <div className="py-1">
            {canSchedule && (
              <button
                type="button"
                className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
                onClick={() => {
                  setMenuOpen(false)
                  setScheduleOpen(true)
                }}
              >
                <Calendar className="h-4 w-4 text-default" />
                <span>Schedule Appointment</span>
              </button>
            )}
            {schedule.reason && (
              <div className="flex w-full items-start gap-x-2 p-3 text-body-sm text-subtle">
                {schedule.state === `locked` ? (
                  <LockSimpleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                ) : (
                  <Calendar className="mt-0.5 h-4 w-4 shrink-0" />
                )}
                <span>
                  Schedule Appointment
                  <span className="block text-label-xs text-minimal">{schedule.reason}</span>
                </span>
              </div>
            )}
            <button
              type="button"
              className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
              onClick={onCustomizeDrawer}
            >
              <ViewColumns3 className="h-4 w-4 rotate-90 text-default" />
              <span>Customize Contact Drawer</span>
            </button>
            {isSuperUser && (
              <>
                <button
                  type="button"
                  className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
                  onClick={handleRebuild}
                >
                  <Refresh className="h-4 w-4 text-default" />
                  <span>Rebuild</span>
                  <div className="rounded-[4px] bg-amber-500/8 p-1">
                    <Crown width={14} height={14} className="text-amber-800" />
                  </div>
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
                  onClick={handleRecalculate}
                  disabled={isRecalculating}
                >
                  <Calculator className="h-4 w-4 text-default" />
                  <span>{isRecalculating ? `Recalculating...` : `Recalculate`}</span>
                  <div className="rounded-[4px] bg-amber-500/8 p-1">
                    <Crown width={14} height={14} className="text-amber-800" />
                  </div>
                </button>
              </>
            )}
            <>
              {!hasMergePermission ? (
                <Tooltip
                  placement="right-start"
                  message="You don't have permission to merge contacts"
                  className="flex w-full cursor-not-allowed items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
                >
                  <LockClosedIcon className="h-4 w-4 text-minimal" />
                  <span className="text-minimal">Merge Contact</span>
                </Tooltip>
              ) : isLinkedPatient ? (
                <div className="flex w-full cursor-not-allowed items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong">
                  <Union className="h-4 w-4 text-minimal" />
                  <span className="text-minimal">Merge Contact</span>
                </div>
              ) : (
                <ContactSearchModalLink contact={contact} className="flex w-full gap-2" title="Merge Contact">
                  <Tooltip
                    placement="right-start"
                    message="Merge this contact with another contact"
                    className="flex w-full !cursor-pointer flex-wrap items-center gap-x-2 p-3 align-middle text-body-sm text-default hover:bg-surface-strong"
                  >
                    <Union className="h-4 w-4 text-default" />
                    <span>Merge Contact</span>
                  </Tooltip>
                </ContactSearchModalLink>
              )}
            </>
            {isIntegrated && (
              <ContactSearchModalLink contact={contact} className="flex w-full gap-2" requireLinkedPatient>
                <div className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong">
                  <LinkIcon className="h-4 w-4 text-default" />
                  <span>Manage Linked Patient</span>
                </div>
              </ContactSearchModalLink>
            )}
            {contact.patientStatus !== `NonPatient` && (
              <button
                type="button"
                className={classNames(
                  `flex w-full items-center gap-2 p-3 text-body-sm`,
                  isLinkedPatient || !hasMarkNonPatientPermission
                    ? `cursor-not-allowed text-minimal`
                    : `text-default hover:bg-surface-strong`,
                )}
                onClick={handleMarkNonPatient}
                disabled={isLinkedPatient || !hasMarkNonPatientPermission}
              >
                {isLinkedPatient || !hasMarkNonPatientPermission ? (
                  <LockClosedIcon className="inline h-4 w-4 text-minimal" />
                ) : (
                  <BoxIcon className="h-4 w-4 text-default" />
                )}
                <Tooltip
                  placement="right-start"
                  message={
                    !hasMarkNonPatientPermission
                      ? `You don't have permission to mark contacts as non-patient`
                      : isLinkedPatient
                        ? `You cannot mark a contact with a linked patient as Non Patient. \nIf you would like to do so, update the status in the PMS and\nit will change this contact status on the next sync.`
                        : undefined
                  }
                  className={classNames(
                    isLinkedPatient || !hasMarkNonPatientPermission ? `cursor-not-allowed` : `cursor-pointer!`,
                  )}
                >
                  <span className={isLinkedPatient || !hasMarkNonPatientPermission ? `text-minimal` : `text-default`}>
                    Mark as Non-Patient
                  </span>
                </Tooltip>
              </button>
            )}
            {!isIntegrated && contact.lifecycleStage !== `Patient` && (
              <MarkPatientModal
                contact={contact}
                className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
                handleMarkPatient={onMarkPatient}
                markPatient
              >
                <UserPlus className="h-4 w-4 cursor-pointer text-default" />
                <span>Mark as Patient</span>
              </MarkPatientModal>
            )}
            {!isIntegrated && contact.lifecycleStage === `Patient` && (
              <>
                <MarkPatientModal
                  contact={contact}
                  className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
                  handleMarkPatient={onMarkPatient}
                >
                  <EditPencil className="h-4 w-4 cursor-pointer text-default" />
                  <span>Update Patient Data</span>
                </MarkPatientModal>
                <button
                  type="button"
                  className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
                  onClick={handleMarkLead}
                >
                  <UserPlus className="h-4 w-4 text-default" />
                  <span>Mark as Lead</span>
                </button>
              </>
            )}
            <button
              type="button"
              className="flex w-full items-center gap-x-2 p-3 text-body-sm text-default hover:bg-surface-strong"
              onClick={showNoteModal}
            >
              <PageEditIcon className="h-4 w-4 text-default" />
              <span>Add Note</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {canSchedule && (
        <ScheduleAppointmentDialog
          contact={contact}
          locationId={contact.locationId!}
          kollaConsumerId={location?.kollaConsumerId ?? undefined}
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
        />
      )}
    </>
  )
})
